import React, { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import TextInput from '../../common/form/TextInput'
import FormRow from '../../common/form/FormRow'
import FormFieldBox from '../../common/form/FormFieldBox'
import SpinnableSubmitButton from '../../common/form/SpinnableSubmitButton'
import { useFormSubmission } from '../../common/servercall/useFormSubmission'
import ErrorAlert from '../../common/components/ErrorAlert'
import { UseFormSetValue } from 'react-hook-form/dist/types/form'
import { VpUser } from '../../app/types'
import { emailRegex } from '../../users/UserForm'

type FormData = {
    firstName: string
    lastName: string
    email: string
}

const schema = yup
    .object()
    .shape({
        firstName: yup.string().trim().max(30, 'too-long').required('required-m'),
        lastName: yup.string().trim().max(30, 'too-long').required('required-m'),
        email: yup.string().trim().max(70, 'too-long').matches(emailRegex, 'invalid-email').nullable(),
    })

type Props = {
    entry: VpUser | null
    onSubmit: (formData: FormData) => void
}

export type ReturnProps = {
    handleSubmit: () => void
    form: () => ReactElement
    submitButton: () => ReactElement
    reset: () => void
    setValue: UseFormSetValue<FormData>
}

const useAdminVpUserForm = ({ entry, onSubmit }: Props): ReturnProps => {
    const { t } = useTranslation()

    const { control, handleSubmit, setError, reset, setValue } = useForm<FormData>({
        resolver: yupResolver(schema),
        defaultValues: entry
            ? { firstName: entry.firstName, lastName: entry.lastName, email: entry.email }
            : {},
    })

    const { submit, isSubmitting, serverError, onError } = useFormSubmission<FormData>(onSubmit, setError)

    return {
        setValue,
        reset,
        handleSubmit: handleSubmit(submit, onError),
        form: () =>
            entry ? (
                <>
                    <ErrorAlert errorKey={typeof serverError === 'string' ? serverError : undefined} showAlert={!!serverError} />
                    <FormRow>
                        <FormFieldBox fullWidth={true}>
                            <TextInput autofocus={true} label={t('users.edit.lastName')} name={'lastName'} control={control} required={true} />
                        </FormFieldBox>
                        <FormFieldBox fullWidth={true}>
                            <TextInput autofocus={false} label={t('users.edit.firstName')} name={'firstName'} control={control} required={true} />
                        </FormFieldBox>
                        <FormFieldBox fullWidth={true}>
                            <TextInput readonly={!!entry.id} autofocus={false} label={t('users.edit.email')} name={'email'} control={control} required={true} />
                        </FormFieldBox>
                    </FormRow>
                </>
            ) : (
                <div />
            ),
        submitButton: () => (entry ? <SpinnableSubmitButton label={t(entry.id ? 'general.form-update' : 'general.form-create')} showSpinner={isSubmitting} /> : <></>),
    }
}

export default useAdminVpUserForm

